import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from 'src/components/ui/command'
import {
  LayoutDashboard,
  Users,
  Phone,
  PhoneCall,
  MessageSquare,
  Clock,
  Moon,
  Sun,
  Monitor
} from 'lucide-react'
import { useRecentSearchData } from 'src/hooks/useRecentSearchData'
import { CAPTION, DATA_MONO } from 'src/styles/typography'
import { cn } from 'src/lib/utils'

export function CommandPalette({ open, setOpen, menus = [] }) {
  const navigate = useNavigate()
  const { setTheme } = useTheme()
  const { numbers, calls, messages } = useRecentSearchData(open)

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [setOpen])

  const handleSelect = (callback) => {
    setOpen(false)
    callback()
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Navigation */}
        <CommandGroup heading="Navigation">
          {menus.filter(item => item.to && item.name).map((item, index) => (
            <CommandItem
              key={index}
              onSelect={() => handleSelect(() => navigate(item.to))}
            >
              {item.icon ? (
                <span className="mr-2 h-4 w-4 flex items-center justify-center">
                  {item.icon}
                </span>
              ) : (
                <LayoutDashboard className="mr-2 h-4 w-4" />
              )}
              <span>{item.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {numbers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Numbers">
              {numbers.map((number) => (
                <CommandItem
                  key={number.id}
                  value={`number ${number.number || ''} ${number.name || ''} ${number.id}`}
                  onSelect={() => handleSelect(() => navigate(`/resources/numbers/active_detail/${number.id}`))}
                >
                  <Phone className="mr-2 h-4 w-4 shrink-0" />
                  <div className="flex flex-col overflow-hidden">
                    <span className={DATA_MONO}>{number.number}</span>
                    {number.name && (
                      <span className={cn(CAPTION, 'truncate')}>{number.name}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {calls.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Calls">
              {calls.map((call) => (
                <CommandItem
                  key={call.id}
                  value={`call ${call.id} ${call.source?.target || ''} ${call.destination?.target || ''}`}
                  onSelect={() => handleSelect(() => navigate(`/resources/calls/calls_detail/${call.id}`))}
                >
                  <PhoneCall className="mr-2 h-4 w-4 shrink-0" />
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm">
                      Call <span className={DATA_MONO}>{call.id}</span>
                    </span>
                    <span className={cn(CAPTION, 'truncate')}>
                      {call.source?.target || '?'} → {call.destination?.target || '?'}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {messages.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Messages">
              {messages.map((message) => {
                const targetAddresses = (message.targets || [])
                  .map((t) => t.destination?.target)
                  .filter(Boolean)
                  .join(' ')

                return (
                  <CommandItem
                    key={message.id}
                    value={`message ${message.id} ${message.source?.target || ''} ${targetAddresses}`}
                    onSelect={() => handleSelect(() => navigate(`/resources/messages/messages_detail/${message.id}`))}
                  >
                    <MessageSquare className="mr-2 h-4 w-4 shrink-0" />
                    <div className="flex flex-col overflow-hidden">
                      <span className="text-sm">
                        SMS <span className={DATA_MONO}>{message.id}</span>
                      </span>
                      <span className={cn(CAPTION, 'truncate')}>
                        {targetAddresses || message.source?.target || '?'} — {message.text}
                      </span>
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        {/* Theme */}
        <CommandGroup heading="Theme">
          <CommandItem onSelect={() => handleSelect(() => setTheme('light'))}>
            <Sun className="mr-2 h-4 w-4" />
            <span>Switch to light mode</span>
          </CommandItem>
          <CommandItem onSelect={() => handleSelect(() => setTheme('dark'))}>
            <Moon className="mr-2 h-4 w-4" />
            <span>Switch to dark mode</span>
          </CommandItem>
          <CommandItem onSelect={() => handleSelect(() => setTheme('system'))}>
            <Monitor className="mr-2 h-4 w-4" />
            <span>Use system theme</span>
          </CommandItem>
        </CommandGroup>

        {/* Recent (TODO: implement recent tracking) */}
        <CommandSeparator />
        <CommandGroup heading="Recent">
          <CommandItem onSelect={() => handleSelect(() => navigate('/dashboard'))}>
            <Clock className="mr-2 h-4 w-4" />
            <span>Dashboard</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>

      {(calls.length > 0 || messages.length > 0) && (
        <div className={cn(CAPTION, 'border-t px-3 py-2')}>
          통화·메시지 검색은 최근 100건까지 포함됩니다. 더 오래된 기록은 목록 페이지에서 확인하세요.
        </div>
      )}
    </CommandDialog>
  )
}

export default CommandPalette
